/**
 * MIN-294 — the new-chat composer composes `AgentChat.Root` (with no thread)
 * + `AgentChat.Settings` + `AgentChat.PromptInput`, rendering every control
 * through the package rather than reaching past it to `elements/prompt-input`.
 *
 * `Root`'s no-thread path is exactly this page's flow: it creates the thread
 * on submit — folding in whatever model `Settings` reports —
 * then sends the first message straight into the new thread's own `Chat` and
 * reports the id through `onThreadChange`, which is where this route
 * navigates. That replaces the old `useCreateChatThread` +
 * `sessionStorage` first-message hand-off to `$threadId.tsx` (the hand-off
 * MIN-295 was opened to remove — it goes here instead, since composing `Root`
 * leaves nothing for it to hand off).
 *
 * No `Context` meter in the footer: there is no thread yet, so there is nothing
 * to meter until the first message lands one.
 */
import { AgentChat } from "@repo/agent-chat";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { AutoFocusComposer } from "@/components/auto-focus-composer";
import { aiModelQueries } from "@/hooks/queries/ai-models";
import { userQueries } from "@/hooks/queries/user";
import { API_BASE_URL } from "@/lib/api-client";
import { medplum } from "@/lib/medplum";

export const Route = createFileRoute("/chat/_layout/")({
  component: NewChatView,
});

function NewChatView() {
  const navigate = useNavigate();
  const [model, setModel] = useState<string | null>(null);

  const { data: aiModels = [] } = useQuery(aiModelQueries.list());
  const { data: user } = useQuery(userQueries.me());

  const models = useMemo(() => aiModels.map((m) => m.model_identifier), [aiModels]);

  // Until the user picks one, the thread runs on this user's configured
  // default — resolved from id to identifier, since that is what the creation
  // body carries.
  const defaultModelId = aiModels.find((m) => m.id === user?.default_model_id)?.model_identifier;
  const resolvedModel = model ?? defaultModelId ?? "";

  return (
    <div className="flex flex-col h-full w-full items-center justify-center">
      <div className="w-full">
        <AgentChat.Root
          auth={medplum}
          baseUrl={API_BASE_URL}
          onThreadChange={(id) => {
            if (id) navigate({ params: { threadId: id }, to: "/chat/$threadId" });
          }}
          threadId={null}
        >
          <AgentChat.Settings model={resolvedModel} models={models} onModelChange={setModel}>
            <AutoFocusComposer />
            <AgentChat.PromptInput footer={<AgentChat.ModelSelector />} placeholder="What would you like to know?" />
          </AgentChat.Settings>
        </AgentChat.Root>
      </div>
    </div>
  );
}
